import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { Logger } from 'src/common/services/Logger';

import {
	queryParamsToFilters,
	updateUrlWithFilters
} from '../services/IssuesPageQueryParamsService';

interface UseFiltersWithQueryParamsParams {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
}

interface UseFiltersWithQueryParamsReturn {
	filters: IFilterOptions;
	handleFilterChange: (newFilters: IFilterOptions) => void;
}

export const useFiltersWithQueryParams = ({
	filterDefinitions,
	filterOptions
}: UseFiltersWithQueryParamsParams): UseFiltersWithQueryParamsReturn => {
	const location = useLocation();
	const history = useHistory();

	const [filters, setFilters] = useState<IFilterOptions>(() => {
		const hasOptions = Object.keys(filterOptions).length > 0;
		const hasDefinitions = filterDefinitions.length > 0;
		const hasSearch = Boolean(location.search);

		if (hasDefinitions && hasOptions && hasSearch) {
			try {
				return queryParamsToFilters(
					new URLSearchParams(location.search),
					filterDefinitions,
					filterOptions
				);
			} catch (error) {
				Logger.error('Failed to parse query parameters for filters during initialization:', error);
				return {};
			}
		}

		return {};
	});

	const lastInitialized = useRef<{ search: string; hasOptions: boolean }>({
		search: location.search,
		hasOptions: Object.keys(filterOptions).length > 0
	});

	useEffect(() => {
		const hasOptions = Object.keys(filterOptions).length > 0;
		const hasDefinitions = filterDefinitions.length > 0;
		const hasSearch = Boolean(location.search);

		// Should initialize if:
		// 1. We have both definitions and options
		// 2. URL has query params
		// 3. Either the URL changed OR we didn't have options before
		const shouldInitialize =
			hasDefinitions &&
			hasOptions &&
			hasSearch &&
			(location.search !== lastInitialized.current.search || !lastInitialized.current.hasOptions);

		if (shouldInitialize) {
			try {
				const initialFilters = queryParamsToFilters(
					new URLSearchParams(location.search),
					filterDefinitions,
					filterOptions
				);

				setFilters(initialFilters);
				lastInitialized.current = {
					search: location.search,
					hasOptions: true
				};
			} catch (error) {
				Logger.error('Failed to parse query parameters for filters:', error);
				// Reset to empty filters on error to prevent breaking the page
				setFilters({});
				lastInitialized.current = {
					search: location.search,
					hasOptions: true
				};
			}
		}
	}, [filterDefinitions, filterOptions, location.search]);

	const handleFilterChange = useCallback(
		(newFilters: IFilterOptions): void => {
			setFilters(newFilters);
			updateUrlWithFilters(history, location, newFilters, filterDefinitions);
		},
		[history, location, filterDefinitions]
	);

	return {
		filters,
		handleFilterChange
	};
};
